import _ from 'lodash'

import moment from 'moment/moment'

import i18n from '@/i18n/i18n'
import OrderService from '@/services/admin/OrderService'
import RetailerDebtService from '@/services/admin/RetailerDebtService.js'
import RetailerService from '@/services/admin/RetailerService.js'
import { build_query, errorMessage } from '@/utilities/helpers'

import { PAYMENT_STATUSES } from '@/constants/constants'

import { PAYMENT_STATUS_TYPE } from '@/constants/paymentStatusType'
import { SALES_CHANNEL_TYPE } from '@/constants/salesChannels'

const { t } = i18n.global

export const namespaced = true

export const allowedFilters = [
  'id',
  'name',
  'alias',
  'address.city_id',
  'address.county_id',
  'users.firstname',
  'not_demo',
  'salesChannels.id',
  'categories.id',
  'only_parents',
  'address.city.region_id',
  'brands.id'
]
export const allowedIncludes = 'users,address.city,address.county,address.district,address.neighborhood,parentRetailer'
export const allowedSorts = '-created_at'

export const state = {
  retailersWithDebt: null,
  meta: null,
  debts: {},
  totalPendingPaymentAmounts: null
}

export const mutations = {
  SET_RETAILERS_WITH_DEBT(state, retailers) {
    state.meta = retailers.meta
    state.retailersWithDebt = _.map(retailers.data, o => _.extend({ sub: null }, o))
  },
  SET_RETAILER_OPEN_ORDERS(state, data) {
    let row = state.retailersWithDebt.find(retailer => retailer.id === data.id)
    if (!row) {
      return
    }
    row.sub = data.orders
  },
  SET_TOTAL_PENDING_PAYMENT_AMOUNTS(state, params) {
    state.totalPendingPaymentAmounts = params
  }
}

export const getters = {
  getOpenOrders: state => id => {
    let retailersWithDebt = state.retailersWithDebt.find(retailer => retailer.id === id)
    return retailersWithDebt.sub || false
  }
}

export const actions = {
  getRetailersWithDebt({ commit }, query) {
    query.append = 'total_payable_unreleased_amount,total_payable_released_amount,total_payable_released_amount_due_today,total_blocked_amount,total_fast_payable_amount'
    query.has_debt = true
    query.not_demo = true
    query.has_sales_channel = true

    let newQuery = build_query(
      query,
      ['id', 'has_debt', 'not_demo', 'has_sales_channel'],
      null,
      '-created_at',
      'total_payable_unreleased_amount,total_payable_released_amount,total_payable_released_amount_due_today,total_blocked_amount,total_fast_payable_amount'
    )

    return new Promise(resolve => {
      RetailerService.getRetailers(newQuery).then(response => {
        commit('SET_RETAILERS_WITH_DEBT', response)
        resolve(response)
      })
    })
  },
  getOpenOrders({ dispatch, commit, getters }, id) {
    return new Promise((resolve, reject) => {
      let openOrders = getters.getOpenOrders(id)
      if (!openOrders) {
        const allowedFilters = ['by_retailer_and_sub', 'retailer_payment_status', 'sales_channel']
        const allowedIncludes = 'farmer,payments,payments.collections,retailer,workflows,application,application.dsPotentialFraud'
        const allowedSorts = '-created_at'
        const allowedAppends = 'payable_remaining_amount,is_retailer_payment_possible,sales_channel,is_available_for_fast_retailer_payment'

        let newQuery = build_query(
          {
            by_retailer_and_sub: id,
            sales_channel: [
              SALES_CHANNEL_TYPE.FORWARD_SALE,
              SALES_CHANNEL_TYPE.DIRECT_FORWARD,
              SALES_CHANNEL_TYPE.DIRECT_CASH,
              SALES_CHANNEL_TYPE.FIBABANK,
              SALES_CHANNEL_TYPE.FORWARD_SALE_WITH_RETAILER_DELAYED,
              SALES_CHANNEL_TYPE.ROMANIA_FORWARD_SALE,
              SALES_CHANNEL_TYPE.ROMANIA_FORWARD_SALE_WITH_RETAILER_DELAYED,
              SALES_CHANNEL_TYPE.ROMANIA_PRIVATE_INDIVIDUAL,
              SALES_CHANNEL_TYPE.VIRTUAL_POS,
              SALES_CHANNEL_TYPE.V4
            ].join(','),
            retailer_payment_status: [
              PAYMENT_STATUSES.OPEN,
              PAYMENT_STATUSES.PARTLY_PAID,
              PAYMENT_STATUSES.BLOCKED
            ].join(','),
            limit: 1000
          },
          allowedFilters,
          allowedIncludes,
          allowedSorts,
          allowedAppends
        )

        OrderService.getOrders(newQuery)
          .then(response => {
            response.data.forEach(order => {
              order.payment_status_badge = PAYMENT_STATUS_TYPE.PAYABLE

              if (order.retailer_payment_status === PAYMENT_STATUS_TYPE.BLOCKED) {
                order.payment_status_badge = PAYMENT_STATUS_TYPE.BLOCKED
              } else {
                if (typeof order.workflows !== 'undefined' && order.workflows.length > 0) {
                  for (let workflow of order.workflows) {
                    if (workflow.pivot.is_required_for_payment && !workflow.pivot.is_done) {
                      order.payment_status_badge = PAYMENT_STATUS_TYPE.WORKFLOWS_NOT_COMPLETED
                      break
                    }
                  }
                }

                const releaseDate = moment(order.retailer_payment_release_date).format('YYYY-MM-DD')
                const today = moment().format('YYYY-MM-DD')
                if (releaseDate > today) {
                  order.payment_status_badge = PAYMENT_STATUS_TYPE.FUTURE_DATED
                }
              }

              if (order.is_available_for_fast_retailer_payment === true) {
                order.payment_status_badge = PAYMENT_STATUS_TYPE.PAYABLE
              }
            })
            commit('SET_RETAILER_OPEN_ORDERS', {
              id: id,
              orders: response.data
            })
            resolve(response)
          })
          .catch(err => {
            const notification = {
              type: 'danger',
              message:
                t('store.shared.fetching_problem', { element: t('store.admin.retailer_debt.payable_orders') }) +
                errorMessage(err)
            }
            dispatch('notification/add', notification, {
              root: true
            })
            reject(err)
          })
      }
    })
  },
  getTotalPendingPaymentAmounts({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      RetailerDebtService.getTotalPendingPaymentAmounts()
        .then(response => {
          commit('SET_TOTAL_PENDING_PAYMENT_AMOUNTS', response.data)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message:
              t('store.shared.fetching_problem', { element: t('store.admin.retailer_debt.total_pending_payments') }) +
              errorMessage(err)
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  }
}
